import React, { Component } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Bar } from "recharts";
import { CommonLoader } from "../../../common/CommonLoader";
import { formatAmount } from "../../../../services/utils";

class PaperStatsChartLineTypeCompare extends Component {

  getCurrentYearMonth() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Ensure two digits
    return `${year}-${month}`;
  }

  formatData = () => {
    const { data1, data2, year, frequency, activeTab, showCompare, startMonth, endMonth } = this.props;

    const NbrInscrit = [];
    const Ca = [];

    if (frequency === "MONTHLY") {
      // Month names for formatting
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

      // Get all unique months from both datasets
      let allMonths = Object.keys(data1 || {}).sort();

      if (startMonth && endMonth) {
        allMonths = allMonths.filter(monthKey => {
          const [, month] = monthKey.split("-");
          return parseInt(month, 10) >= parseInt(startMonth, 10) &&
            parseInt(month, 10) <= parseInt(endMonth, 10);
        });
      }

      allMonths.forEach(monthKey => {
        // --- DATASET 1 ---
        const silots1 = data1[monthKey] || {};
        let individuelNbrTotal1 = 0;
        let fiduciaireNbrTotal1 = 0;
        let individuelCaTotal1 = 0;
        let fiduciaireCaTotal1 = 0;

        Object.values(silots1).forEach(silot => {
          individuelNbrTotal1 += silot.individuelNbrInscrit || 0;
          fiduciaireNbrTotal1 += silot.fiduciareNbrInscrit || 0;
          individuelCaTotal1 += silot.individuelCa || 0;
          fiduciaireCaTotal1 += silot.fiduciareCa || 0;
        });

        // --- DATASET 2 (previous year, e.g., 2024) ---
        const [yearPart, monthPart] = monthKey.split("-");
        const prevYearKey = `${year - 1}-${monthPart}`;
        const silots2 = data2[prevYearKey] || {};

        let individuelNbrTotal2 = 0;
        let fiduciaireNbrTotal2 = 0;
        let individuelCaTotal2 = 0;
        let fiduciaireCaTotal2 = 0;

        Object.values(silots2).forEach(silot => {
          individuelNbrTotal2 += silot.individuelNbrInscrit || 0;
          fiduciaireNbrTotal2 += silot.fiduciareNbrInscrit || 0;
          individuelCaTotal2 += silot.individuelCa || 0;
          fiduciaireCaTotal2 += silot.fiduciareCa || 0;
        });

        // Convert month number → month name
        const monthIndex = parseInt(monthPart, 10) - 1;
        const formattedMonth = monthNames[monthIndex];

        // Push combined NbrInscrit data
        NbrInscrit.push({
          name: formattedMonth,
          Individuel1: individuelNbrTotal1,
          Fiduciaire1: fiduciaireNbrTotal1,
          Individuel2: individuelNbrTotal2,
          Fiduciaire2: fiduciaireNbrTotal2,
          Total1: individuelNbrTotal1 + fiduciaireNbrTotal1,
          Total2: individuelNbrTotal2 + fiduciaireNbrTotal2,
        });

        // Push combined CA data (divide by 100 for both)
        Ca.push({
          name: formattedMonth,
          Individuel1: individuelCaTotal1 / 100,
          Fiduciaire1: fiduciaireCaTotal1 / 100,
          Individuel2: individuelCaTotal2 / 100,
          Fiduciaire2: fiduciaireCaTotal2 / 100,
          Total1: (individuelCaTotal1 + fiduciaireCaTotal1) / 100,
          Total2: (individuelCaTotal2 + fiduciaireCaTotal2) / 100,
        });
      });
    }

    if (frequency === "DAILY") {
      const formatKey = (dateKey) => dateKey.slice(5); // get "MM-DD"

      // Get all unique days (month+day) from both datasets
      const allDays = Array.from(
        new Set([
          ...Object.keys(data1 || {}).map(formatKey),
          ...Object.keys(data2 || {}).map(formatKey),
        ])
      ).sort();

      allDays.forEach(dayKey => {
        // --- Find matching days in both datasets ---
        const key1 = Object.keys(data1 || {}).find(k => k.endsWith(dayKey));
        const key2 = Object.keys(data2 || {}).find(k => k.endsWith(dayKey));

        const silots1 = (key1 && data1[key1]) || {};
        const silots2 = (key2 && data2[key2]) || {};

        // Totals for dataset 1
        let individuelNbrTotal1 = 0;
        let fiduciaireNbrTotal1 = 0;
        let individuelCaTotal1 = 0;
        let fiduciaireCaTotal1 = 0;

        Object.values(silots1).forEach(silot => {
          individuelNbrTotal1 += silot.individuelNbrInscrit || 0;
          fiduciaireNbrTotal1 += silot.fiduciareNbrInscrit || 0;
          individuelCaTotal1 += silot.individuelCa || 0;
          fiduciaireCaTotal1 += silot.fiduciareCa || 0;
        });

        // Totals for dataset 2
        let individuelNbrTotal2 = 0;
        let fiduciaireNbrTotal2 = 0;
        let individuelCaTotal2 = 0;
        let fiduciaireCaTotal2 = 0;

        Object.values(silots2).forEach(silot => {
          individuelNbrTotal2 += silot.individuelNbrInscrit || 0;
          fiduciaireNbrTotal2 += silot.fiduciareNbrInscrit || 0;
          individuelCaTotal2 += silot.individuelCa || 0;
          fiduciaireCaTotal2 += silot.fiduciareCa || 0;
        });

        // Format day label: "DD/MM"
        const [monthPart, dayPart] = dayKey.split("-");
        const formattedDay = `${dayPart}/${monthPart}`;

        // Push NbrInscrit
        NbrInscrit.push({
          name: formattedDay,
          Individuel1: individuelNbrTotal1,
          Fiduciaire1: fiduciaireNbrTotal1,
          Individuel2: individuelNbrTotal2,
          Fiduciaire2: fiduciaireNbrTotal2,
          Total1: individuelNbrTotal1 + fiduciaireNbrTotal1,
          Total2: individuelNbrTotal2 + fiduciaireNbrTotal2,
        });

        // Push CA (divide by 100)
        Ca.push({
          name: formattedDay,
          Individuel1: individuelCaTotal1 / 100,
          Fiduciaire1: fiduciaireCaTotal1 / 100,
          Individuel2: individuelCaTotal2 / 100,
          Fiduciaire2: fiduciaireCaTotal2 / 100,
          Total1: (individuelCaTotal1 + fiduciaireCaTotal1) / 100,
          Total2: (individuelCaTotal2 + fiduciaireCaTotal2) / 100,
        });
      });
    }


    if (frequency === "YEARLY") { }

    return activeTab === "chiffreAffaire" ? Ca : NbrInscrit;

  };

  formatCompareData = (data, showCompare) => {
    if (showCompare.all) {
      // Keep everything (no filtering)
      return data;
    }

    return data.map(item => {
      const base = { name: item.name };

      if (showCompare.individuel) {
        const Total1 = item.Individuel1;
        const Total2 = item.Individuel2;
        return {
          ...base,
          Individuel1: item.Individuel1,
          Individuel2: item.Individuel2,
          Total1,
          Total2
        };
      }

      if (showCompare.fiduciare) {
        const Total1 = item.Fiduciaire1;
        const Total2 = item.Fiduciaire2;
        return {
          ...base,
          Fiduciaire1: item.Fiduciaire1,
          Fiduciaire2: item.Fiduciaire2,
          Total1,
          Total2
        };
      }

      // Default (none selected)
      return base;
    });
  };


  render() {
    const { visibleKeys, showCompare, year } = this.props;
    let chartData = this.formatData();

    chartData = showCompare ? this.formatCompareData(chartData, showCompare) : chartData;


    const legendPayload = [
      { value: year, type: 'line', color: '#000', id: 'Total1' },
      { value: year - 1, type: 'line', color: '#464646b3', id: 'Total2' },
    ];

    return (
      <React.Fragment>
        {this.props.fetching ? <div style={{ width: '100%' }}><CommonLoader containerHeight={"300px"} iconFontSize={"50px"} iconColor={"#29394D"} /></div> : (
          <ResponsiveContainer width={"100%"} height={400}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 30 }}>
              <CartesianGrid stroke="#D8DDE2" vertical={false} strokeDasharray="4" />
              <XAxis dataKey="name" tickMargin={10} axisLine={false} tickLine={false} tick={{ fill: '#6D7F92' }} />
              <YAxis tickMargin={10} axisLine={false} tickLine={false} tick={{ fill: '#6D7F92' }} allowDecimals={false} />
              <Tooltip content={({ active, payload, label }) => {
                if (active) {
                  return (
                    <div style={{
                      backgroundColor: '#ffffff',
                      padding: '10px',
                      borderRadius: '5px',
                      border: '1px solid gray'
                    }}>
                      <p>{label}</p>
                      {
                        payload && payload.filter(p => ["Total1", "Total2"].includes(p.dataKey)).map(p => (
                          <p key={p.dataKey}
                            style={{ color: p.stroke, fontSize: '14px' }}>
                            {legendPayload.find(legend => legend.id === p.dataKey).value}: {this.props.activeTab === 'chiffreAffaire' ? formatAmount(p.value, false) : p.value}
                          </p>
                        ))
                      }
                    </div>
                  );
                }
                return null;
              }} />
              <Legend payload={legendPayload} iconType="line" />

              {visibleKeys.includes('fiduciare') && <Bar dataKey="Fiduciaire2" stackId="b" fill="#ff7300" name="Fiduciaire 2024" opacity={0.5} />}
              {visibleKeys.includes('individuel') && <Bar dataKey="Individuel2" stackId="b" fill="#007bff" name="Individuel 2024" opacity={0.5} />}
              {visibleKeys.includes('fiduciare') && <Bar dataKey="Fiduciaire1" stackId="a" fill="#ff7300" name="Fiduciaire 2025" />}
              {visibleKeys.includes('individuel') && <Bar dataKey="Individuel1" stackId="a" fill="#007bff" name="Individuel 2025" />}

              <Line type="monotone" dataKey="Total1" stroke="#000" name="Total 2025" strokeWidth={2} isAnimationActive={true} animationDuration={2000} animationEasing="ease-out" />
              <Line type="monotone" dataKey="Total2" stroke="#464646b3" name="Total 2024" strokeWidth={2} isAnimationActive={true} animationDuration={2000} animationEasing="ease-out" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </React.Fragment>
    );
  }
}

export default PaperStatsChartLineTypeCompare;



// WEBPACK FOOTER //
// ./app/components/Admin/EventStats/EventRegistrantLinechart/PaperStatsChartLineTypeCompare.js